import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Coins, Loader2, Copy, Trash2, CheckCircle2, ExternalLink, Image as ImageIcon,
         Megaphone, FileText, Video, TrendingUp, Wand2, Rocket } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime, fileUrl } from "../../lib/api";
import { PageHeader, StatCard, EmptyState, LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";

const CATS = [
  { k: "image", l: "AI Image Creation", icon: ImageIcon },
  { k: "marketing", l: "AI Marketing", icon: Megaphone },
  { k: "content", l: "AI Content", icon: FileText },
  { k: "video", l: "AI Video Content", icon: Video },
  { k: "growth", l: "AI Business Growth", icon: TrendingUp },
];

export default function GrowthAi() {
  const [tools, setTools] = useState(null);
  const [cat, setCat] = useState("marketing");
  const [tool, setTool] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [library, setLibrary] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [creditsData, setCreditsData] = useState(null);
  const [buyRef, setBuyRef] = useState({ package_id: "", reference: "" });

  const load = () => {
    api.get("/store-admin/ai/growth/tools").then((r) => setTools(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/store-admin/ai/growth/library").then((r) => setLibrary(r.data.items)).catch(() => setLibrary([]));
    api.get("/store-admin/ai/credits").then((r) => setCreditsData(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!tools) return <LoadingRows rows={5} />;
  const shown = tools.tools.filter((t) => t.cat === cat);

  const run = async () => {
    if (!tool) return;
    setBusy(true); setResult(null);
    try {
      const { data: r } = await api.post(`/store-admin/ai/growth/${tool.kind === "image" ? "image" : "generate"}`, { tool: tool.id, prompt });
      setResult({ ...r, kind: tool.kind });
      setTools((t) => ({ ...t, balance: r.balance }));
      toast.success(`${r.label} ready — ${r.credits} credits used`);
      api.get("/store-admin/ai/growth/library").then((x) => setLibrary(x.data.items));
    } catch (e) {
      if (e?.response?.data?.detail === "NO_CREDITS") { toast.error("Not enough AI credits — please top up"); setBuyOpen(true); }
      else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const copyText = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  const delItem = async (id) => {
    await api.delete(`/store-admin/ai/growth/library/${id}`).catch((e) => toast.error(errMsg(e)));
    setLibrary((l) => l.filter((i) => i.id !== id));
    toast.success("Removed");
  };

  const buy = async () => {
    if (!buyRef.package_id) { toast.error("Choose a package first"); return; }
    setBusy(true);
    try {
      await api.post("/store-admin/ai/credits/purchase", buyRef);
      toast.success("Purchase submitted for verification");
      setBuyOpen(false); setBuyRef({ package_id: "", reference: "" });
      api.get("/store-admin/ai/credits").then((r) => setCreditsData(r.data));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="growth-ai-page">
      <PageHeader title="Business Growth AI" subtitle="Marketing, content, video scripts, creatives and growth ideas — powered by MILTROO AI." testid="growth-ai-title" />

      <div className="flex flex-wrap items-center gap-3 mb-8">
        <button data-testid="growth-credits-balance" onClick={() => setBuyOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 px-4 py-2 text-sm font-bold hover:bg-emerald-500/20 transition-colors">
          <Coins className="h-4 w-4" /> {tools.balance} AI Credits — Top Up
        </button>
        <Link to="/store/ai-studio" data-testid="growth-open-studio"
          className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-bold hover:bg-muted transition-colors">
          <Sparkles className="h-4 w-4" /> Open Website Design Studio
        </Link>
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        {CATS.map((c) => (
          <button key={c.k} data-testid={`growth-cat-${c.k}`} onClick={() => { setCat(c.k); setTool(null); setResult(null); }}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold border transition-colors ${cat === c.k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
            <c.icon className="h-4 w-4" /> {c.l}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-1 grid sm:grid-cols-2 lg:grid-cols-1 gap-3">
          {shown.map((t) => (
            <button key={t.id} data-testid={`tool-${t.id}`} onClick={() => { setTool(t); setResult(null); }}
              className={`text-left rounded-2xl border p-4 transition-all ${tool?.id === t.id ? "border-primary ring-2 ring-primary/25 bg-card" : "border-border bg-card hover:border-foreground/25"}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-bold text-sm">{t.label}</p>
                <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 shrink-0">{t.credits} cr</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{t.hint}</p>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 space-y-6">
          {tool ? (
            <div data-testid="growth-workspace" className="bg-card border border-border rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold text-xl tracking-tight flex items-center gap-2"><Wand2 className="h-5 w-5" /> {tool.label}</h3>
                <span data-testid="growth-estimate" className="text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 rounded-full px-3 py-1">Est. {tool.credits} credits</span>
              </div>
              <textarea data-testid="growth-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
                placeholder={tool.hint}
                className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:border-primary resize-none" />
              <Button data-testid="growth-generate" onClick={run} disabled={busy}
                className="w-full h-12 rounded-xl font-bold text-base active:scale-[0.98] transition-transform">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                {busy ? "Generating..." : `Generate (${tool.credits} credits)`}
              </Button>

              {result && (
                <div data-testid="growth-result" className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 space-y-3">
                  <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4" /> {result.label} — saved to your AI Library · {result.credits} credits used
                  </p>
                  {result.kind === "text" ? (
                    <>
                      <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed max-h-96 overflow-y-auto">{result.output}</pre>
                      <Button data-testid="growth-copy" variant="outline" onClick={() => copyText(result.output)} className="rounded-full font-bold">
                        <Copy className="h-4 w-4 mr-2" /> Copy
                      </Button>
                    </>
                  ) : (
                    <>
                      <img data-testid="growth-image-result" src={fileUrl(result.url)} alt={result.label} className="w-full max-w-md rounded-xl border border-border" />
                      <div className="flex gap-2">
                        <a data-testid="growth-image-open" href={fileUrl(result.url)} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-full border border-border px-4 h-9 text-xs font-bold hover:bg-muted">
                          <ExternalLink className="h-3.5 w-3.5" /> Open Full Size
                        </a>
                        <Button data-testid="growth-image-copy" variant="outline" onClick={() => copyText(fileUrl(result.url))} className="rounded-full font-bold h-9 text-xs">
                          <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy URL
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-card border border-dashed border-border rounded-2xl p-10 text-center">
              <Rocket className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <p className="font-bold">Pick a tool to start</p>
              <p className="text-sm text-muted-foreground mt-1">Choose any AI tool on the left — credits are shown upfront and charged only on success.</p>
            </div>
          )}

          <div>
            <h3 className="font-display font-bold text-xl tracking-tight mb-4">AI Library</h3>
            {library === null ? <div className="bg-card border border-border rounded-2xl p-6"><LoadingRows rows={2} /></div> :
             library.length === 0 ? <EmptyState icon={FileText} title="Library is empty" body="Everything you generate is saved here for reuse." /> : (
              <div data-testid="growth-library" className="space-y-2">
                {library.slice(0, 20).map((i) => (
                  <div key={i.id} data-testid={`lib-${i.id}`} className="bg-card border border-border rounded-xl p-4 flex items-center gap-4">
                    {i.kind === "image" && <img src={fileUrl(i.output)} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-sm">{i.label}</p>
                      <p className="text-xs text-muted-foreground truncate">{i.prompt || i.output.slice(0, 90)}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{fmtDateTime(i.created_at)}</p>
                    </div>
                    <button data-testid={`lib-copy-${i.id}`} onClick={() => copyText(i.kind === "image" ? fileUrl(i.output) : i.output)}
                      className="h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:bg-muted shrink-0"><Copy className="h-3.5 w-3.5" /></button>
                    <button data-testid={`lib-del-${i.id}`} onClick={() => delItem(i.id)}
                      className="h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:bg-red-500/10 hover:text-red-500 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {buyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setBuyOpen(false)}>
          <div data-testid="growth-buy-modal" className="bg-card border border-border rounded-2xl p-6 w-full max-w-lg space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Coins className="h-5 w-5" /> Buy AI Credits</h3>
            <div className="grid grid-cols-2 gap-3">
              {(creditsData?.packages || []).map((p) => (
                <button key={p.id} data-testid={`growth-pkg-${p.key}`} onClick={() => setBuyRef({ ...buyRef, package_id: p.id })}
                  className={`rounded-xl border p-4 text-left transition-colors ${buyRef.package_id === p.id ? "border-primary ring-2 ring-primary/25" : "border-border hover:border-foreground/25"}`}>
                  <p className="font-display font-extrabold text-xl">{p.credits} <span className="text-xs font-bold text-muted-foreground">credits</span></p>
                  <p className="text-xs text-muted-foreground">{p.name}</p>
                  <p className="text-sm font-bold mt-1">{fmtINR(p.price)}</p>
                </button>
              ))}
            </div>
            {buyRef.package_id && (
              <div className="space-y-3">
                {creditsData?.payment_link && (
                  <a data-testid="growth-pay-link" href={creditsData.payment_link} target="_blank" rel="noopener noreferrer"
                    className="w-full h-11 rounded-xl bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center gap-2">
                    Pay on SuperProfile <ExternalLink className="h-4 w-4" />
                  </a>
                )}
                <input data-testid="growth-pay-ref" value={buyRef.reference} onChange={(e) => setBuyRef({ ...buyRef, reference: e.target.value })}
                  placeholder="UTR / Transaction Reference *"
                  className="w-full h-11 rounded-lg border border-input bg-background px-3 text-sm" />
                <Button data-testid="growth-pay-submit" onClick={buy} disabled={busy} className="w-full h-11 rounded-xl font-bold">
                  {busy ? "Submitting..." : "I Have Paid — Submit for Verification"}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
